import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
  useTheme,
  useMediaQuery,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useAppContext } from '../context/AppContext';
import splashImage from '../assets/man-arriving-gaming-room-ready-play-science-fiction-videogame-pc-with-his-friends.jpg';
import contactDataCustomerService from '../static/contactDataCustomerService.json';
import './Erstinformation.css';
import Loading from '../components/Loading';
import * as yup from 'yup';
import { useYupValidation } from '../hooks/useYupValidation';

/*
Es scheint, dass das Styling über den nested Selektor im `sx`-Prop nicht funktioniert.
Bei Material-UI gibt es manchmal Besonderheiten bei der Anwendung von Styles auf innere Komponenten.
Darum diese Lösung für das Outline der Checkbox (auch für den IconButton übernommen):
 */
const StyledCheckbox = styled(Checkbox)(() => ({
  '&.Mui-focusVisible': {
    outline: '3px solid var(--color-tertiary)',
    outlineOffset: '2px',
    borderRadius: '4px',
  },
}));

const StyledIconButton = styled(IconButton)(() => ({
  '&.Mui-focusVisible': {
    outline: '3px solid var(--color-tertiary)',
    outlineOffset: '2px',
    borderRadius: '4px',
  },
}));

const validationRules = yup.object().shape({
  erstinformationRead: yup
    .boolean()
    .required('Bitte bestätige, dass du die Dokumente erhalten und gelesen hast.')
    .oneOf([true], 'Bitte bestätige, dass du die Dokumente erhalten und gelesen hast.'),
});

const Erstinformation = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { state, dispatch } = useAppContext();

  const [checkboxValue, setCheckboxValue] = useState(false);
  const { errors, fieldRefs, validateField, validateForm } = useYupValidation(validationRules);
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentFilename, setDocumentFilename] = useState('');
  const [loading, setLoading] = useState(true);

  const initializePage = useCallback(async () => {
    try {
      // Setze globale Locale (falls erforderlich)
      await setGlobalLocale();

      // Lade Produktdokumente
      const apiResult = await getProductDocuments();

      if (apiResult.success && apiResult.documents.length > 0) {
        const firstDocument = apiResult.documents[0];
        setDocumentUrl(firstDocument.url);
        setDocumentFilename(firstDocument.filename || firstDocument.url);

        // Update App State
        dispatch({
          type: 'SET_DOCUMENT_LINKS',
          payload: {
            linkErstinformation: firstDocument.url,
            filenameErstinformation: firstDocument.filename || firstDocument.url,
          },
        });
      }
    } catch (error) {
      console.error('Fehler beim Initialisieren der Seite:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    // Prüfe Cookie für bereits gelesene Erstinformation
    const cookieValue = getCookie('readErstinformation');

    if (cookieValue === 'true') {
      navigate('/tarif-berechnen');
    }
  }, [navigate]);

  useEffect(() => {
    initializePage();
  }, [initializePage]);

  const setGlobalLocale = async () => {
    // Implementierung für globale Locale-Einstellung
    return Promise.resolve();
  };

  const getProductDocuments = async () => {
    // Mock-Implementierung für API-Aufruf
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          success: true,
          documents: [
            {
              url: 'https://static.assona.com/RuV-Hardware/RUV_HW_2025/R+V_DE_Erst_Kundeninformation_Hardware_Versicherung.pdf',
              filename: 'Erstinformation Hardware-Versicherung',
            },
          ],
        });
      }, 500);
    });
  };

  const getCookie = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift();
    return null;
  };

  const setCookie = (name, value) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 Tage
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const handleDownload = async () => {
    if (documentUrl) {
      try {
        // Simuliere Download
        const link = document.createElement('a');
        link.href = documentUrl;
        link.download = documentFilename || 'erstinformation.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Fehler beim Download:', error);
      }
    }
  };

  const handleCheckboxChange = event => {
    const checked = event.target.checked;
    setCheckboxValue(checked);
  };

  const handleBlur = async event => {
    const { name, value, type, checked } = event.target;
    await validateField(name, type === 'checkbox' ? checked : value);
  };

  const handleContinue = async () => {
    if (!(await validateForm({ erstinformationRead: checkboxValue }))) {
      return;
    }

    // Cookie setzen
    setCookie('readErstinformation', state.checkboxErstinformation?.toString() || 'true');
    navigate('/tarif-berechnen');
  };

  const handleTouchOutside = () => {
    // Focus Management
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${contactDataCustomerService.email}`;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box className="erstinformation-container" onClick={handleTouchOutside}>
      {/* Hauptinhalt */}
      <Box className="erstinformation-content">
        <Container className="erstinformation-main-container">
          {/* Flex Container - Responsive */}
          <Box className="erstinformation-flex-container">
            {/* Linke Seite - Hauptinhalt */}
            <Box className="erstinformation-main-content" id="jumpmark-maincontent" tabIndex={-1}>
              {/* Überschrift */}
              <Typography className="erstinformation-title text-white text-24-bold" variant="h1" component="h1">
                Hardwareversicherung
              </Typography>

              {/* Willkommen Text */}
              <Typography className="erstinformation-subtitle text-white h1-style" variant="h2" component="h2">
                Willkommen bei der assona - wir sind Partner der R+V
              </Typography>

              {/* Beschreibungstext */}
              <Typography
                className="erstinformation-description body-text-large-normal text-white"
                variant="body1"
                component="p"
              >
                Du wurdest zu uns weitergeleitet, da uns die R+V als Versicherungsvermittler beauftragt hat.
              </Typography>

              {/* Download-Bereich */}
              <Box className="erstinformation-download-container body-text-large-bold text-white" variant="body1">
                <StyledIconButton
                  className="erstinformation-download-button text-white"
                  aria-label="Erstinformationen herunterladen"
                  onClick={handleDownload}
                >
                  <FileDownloadOutlinedIcon sx={{ fontSize: 30 }} />
                </StyledIconButton>

                <Typography
                  className="erstinformation-download-text body-text-large-bold text-white"
                  onClick={handleDownload}
                >
                  Erstinformationen herunterladen
                </Typography>
              </Box>

              {/* Checkbox-Bereich */}
              <Box
                className={`erstinformation-checkbox-container 
                ${errors.erstinformationRead ? 'erstinformation-checkbox-container-error' : ''} 
                ${checkboxValue ? 'erstinformation-checkbox-container-checked' : ''}`}
              >
                <FormControlLabel
                  className="erstinformation-checkbox"
                  control={
                    <StyledCheckbox
                      name="erstinformationRead"
                      className="erstinformation-checkbox-icon text-white"
                      checked={checkboxValue}
                      onChange={handleCheckboxChange}
                      onBlur={handleBlur}
                      slotProps={{
                        input: {
                          ref: fieldRefs.erstinformationRead,
                          'aria-invalid': !!errors.erstinformationRead,
                          'aria-describedby': errors.erstinformationRead ? 'erstinfo-error-message' : null,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      className="erstinformation-checkbox-label body-text-large-bold text-white"
                      variant="body1"
                      //style={{}}
                    >
                      Ich bestätige, die Erstinformationen heruntergeladen und gelesen zu haben.
                    </Typography>
                  }
                  sx={{
                    alignItems: 'flex-start',
                    ml: 0,
                  }}
                />
              </Box>

              {/* Fehlermeldung */}
              <Box className="erstinformation-error-container">
                {errors.erstinformationRead && (
                  <Alert
                    id="erstinfo-error-message" // ID für aria-describedby
                    className="erstinformation-error-text error-text-small-bold text-error"
                    severity="error"
                    icon={false}
                    aria-live="assertive"
                    role="alert"
                    aria-atomic="true"
                  >
                    {errors.erstinformationRead}
                  </Alert>
                )}
              </Box>

              {/* Button */}
              <Box className="erstinformation-action-container">
                <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleContinue}
                    aria-label="Jetzt Tarif berechnen"
                    sx={{
                      backgroundColor: 'var(--color-tertiary)',
                      color: 'var(--color-primary-text)',
                      px: 3,
                      py: 1,
                      borderRadius: '3px',
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'var(--color-accent-1)',
                        opacity: 0.9,
                      },
                      '&:focus': {
                        outline: '2px solid var(--color-primary-text)',
                        outlineOffset: '2px',
                      },
                      '&:focus-visible': {
                        outline: '2px solid var(--color-secondary-text)!important',
                        outlineOffset: '3px',
                      },
                    }}
                  >
                    Jetzt Tarif berechnen
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Rechte Seite - Bild (nur Desktop) */}
            {!isMobile && !isTablet && (
              <Box className="erstinformation-image-container">
                <Box
                  className="erstinformation-image"
                  component="img"
                  src={splashImage}
                  alt=""
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Kontakt-Information */}
          <Container className="erstinformation-contact-container" maxWidth={false}>
            <Typography className="erstinformation-contact-text body-text-medium-normal" variant="body1">
              Du möchtest die Erstinformation in Papierform? Gerne schicken wir dir diese per Post zu. Bitte wende dich
              hierfür an uns.
              <br />
              {`${contactDataCustomerService.company}, ${contactDataCustomerService.postbox}, ${contactDataCustomerService.postalCode} ${contactDataCustomerService.city}, Telefon ${contactDataCustomerService.phone}, E-Mail `}
              <Link className="erstinformation-contact-link" variant="body2" onClick={handleEmailClick}>
                {contactDataCustomerService.email}
              </Link>
            </Typography>
          </Container>
        </Container>
      </Box>
    </Box>
  );
};

export default Erstinformation;
